import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import { useTabParam } from "../hooks/useTabParam";
import BottomNav from "../components/BottomNav";
import LeaveCalendar from "../components/LeaveCalendar";
import { useAuth } from "../context/AuthContext";
import type {
  LeaveEntitlement,
  LeaveRequest,
  LeaveType,
} from "../types";
import {
  formatLeaveStatus,
  formatLeaveTypeName,
  leaveStatusTone,
} from "../utils/formatters";

const LEAVE_TABS = ["request", "my", "balance", "team"] as const;

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

export default function LeavePage() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const isManagerOrAbove = employee
    ? employee.is_admin || employee.can_approve_leave || employee.can_view_team
    : false;

  const [tab, setTab] = useTabParam(LEAVE_TABS, "request");

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t("leave.title")}
        </h1>
      </header>

      <nav className="flex overflow-x-auto border-b border-gray-200 px-2 text-sm">
        <TabBtn active={tab === "request"} onClick={() => setTab("request")}>
          {t("leave.requestLeave")}
        </TabBtn>
        <TabBtn active={tab === "my"} onClick={() => setTab("my")}>
          {t("leave.myRequests")}
        </TabBtn>
        <TabBtn active={tab === "balance"} onClick={() => setTab("balance")}>
          {t("leave.myBalance")}
        </TabBtn>
        {isManagerOrAbove && (
          <TabBtn active={tab === "team"} onClick={() => setTab("team")}>
            {t("leave.teamRequests")}
          </TabBtn>
        )}
      </nav>

      <main className="flex-1 overflow-y-auto px-5 py-4 pb-24">
        {tab === "request" && <RequestTab onSubmitted={() => setTab("my")} />}
        {tab === "my" && <MyRequestsTab />}
        {tab === "balance" && <BalanceTab />}
        {tab === "team" && isManagerOrAbove && <TeamRequestsTab />}
      </main>

      <BottomNav />
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "whitespace-nowrap px-3 py-2 text-sm font-semibold",
        active
          ? "border-b-2 border-pme-blue text-pme-blue"
          : "text-gray-500 hover:text-gray-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ---------- Request tab ----------

function RequestTab({ onSubmitted }: { onSubmitted: () => void }) {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const regionId = employee?.region_id ?? null;

  const [types, setTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveEntitlement[]>([]);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [t1, b1] = await Promise.all([
          api.get<LeaveType[]>("/api/leave/types"),
          api.get<LeaveEntitlement[]>("/api/leave/balances"),
        ]);
        setTypes(t1);
        setBalances(b1);
        if (t1.length && typeId === null) setTypeId(t1[0].id);
      } catch (err) {
        setError((err as Error).message || "Failed to load leave data");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSingleDay = startDate === endDate;
  const balanceForType = balances.find((b) => b.leave_type_id === typeId);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!typeId || cooldown) return;
    setSubmitting(true);
    setError(null);
    setToast(null);
    try {
      await api.post("/api/leave/requests", {
        leave_type_id: typeId,
        start_date: startDate,
        end_date: endDate,
        is_half_day_start: halfStart,
        is_half_day_end: halfEnd,
        reason: reason || null,
      });
      setTypeId(types.length ? types[0].id : null);
      setReason("");
      setStartDate("");
      setEndDate("");
      setHalfStart(false);
      setHalfEnd(false);
      setToast(t("leave.requestSubmitted", "Leave request submitted"));
      setCooldown(true);
      setTimeout(() => setCooldown(false), 3000);
      setTimeout(() => onSubmitted(), 1500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 text-sm">
      <Field label={t("leave.leaveType")}>
        <select
          value={typeId ?? ""}
          onChange={(e) => setTypeId(Number(e.target.value))}
          className="input"
        >
          {types.map((ty) => (
            <option key={ty.id} value={ty.id}>
              {ty.name}
            </option>
          ))}
        </select>
      </Field>

      {/* Calendar date range picker */}
      <LeaveCalendar
        regionId={regionId}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={(s, e) => {
          setStartDate(s);
          setEndDate(e);
        }}
      />

      {/* Half-day toggles */}
      <div className="space-y-2">
        {isSingleDay ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={halfStart}
              onChange={(e) => {
                setHalfStart(e.target.checked);
                setHalfEnd(false);
              }}
            />
            {t("leave.halfDay", "Half day")}
          </label>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={halfStart}
                onChange={(e) => setHalfStart(e.target.checked)}
              />
              {t("leave.firstDayHalf", "First day is half day")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={halfEnd}
                onChange={(e) => setHalfEnd(e.target.checked)}
              />
              {t("leave.lastDayHalf", "Last day is half day")}
            </label>
          </>
        )}
      </div>

      {balanceForType && Number(balanceForType.total_days) > 0 && (
        <p className="text-xs text-gray-500">
          {formatLeaveTypeName(
            balanceForType.leave_type_code,
            balanceForType.leave_type_name,
          )}
          : {balanceForType.remaining_days} /{" "}
          {Number(balanceForType.total_days) +
            Number(balanceForType.carried_over_days) +
            Number(balanceForType.adjustment_days)}{" "}
          {t("leave.balance.remaining")}
        </p>
      )}

      <Field label={t("leave.reason")}>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="input"
        />
      </Field>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {toast && <p className="text-sm text-green-600">{toast}</p>}

      <button
        type="submit"
        disabled={submitting || !typeId || cooldown || !startDate || !endDate}
        className="w-full rounded-lg bg-pme-blue py-3 text-sm font-semibold text-white disabled:opacity-60"
      >
        {submitting
          ? t("common.loading")
          : !startDate || !endDate
            ? t("leave.selectDatesHint", "Select your leave dates")
            : t("leave.submit")}
      </button>
    </form>
  );
}

// ---------- My Requests ----------

function MyRequestsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.get<LeaveRequest[]>("/api/leave/requests"));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const cancel = async (id: number) => {
    if (!confirm("Cancel this leave request?")) return;
    await api.put(`/api/leave/requests/${id}/cancel`, {});
    await load();
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">{t("leave.noRequests")}</p>;
  }

  const acceptAmendment = async (id: number) => {
    await api.put(`/api/leave/requests/${id}/accept-amendment`);
    await load();
  };

  const rejectAmendment = async (id: number) => {
    const reason = prompt(t("leave.declineReason")) || "";
    await api.put(`/api/leave/requests/${id}/reject-amendment`, { reason });
    await load();
  };

  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li
          key={r.id}
          className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)}
              </p>
              <p className="text-xs text-gray-500">
                {DATE_FMT.format(new Date(r.start_date))} →{" "}
                {DATE_FMT.format(new Date(r.end_date))} · {r.total_days}{" "}
                {t("leave.days").toLowerCase()}
              </p>
              {r.reason && (
                <p className="mt-1 text-xs text-gray-600">{r.reason}</p>
              )}
              {r.reviewer_notes && (
                <p className="mt-1 text-xs italic text-gray-500">
                  {r.reviewer_name}: {r.reviewer_notes}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <span
                className={[
                  "rounded-full px-2 py-0.5 text-xs font-semibold",
                  leaveStatusTone(r.status),
                ].join(" ")}
              >
                {formatLeaveStatus(r.status)}
              </span>
              {(r.status === "pending" || r.status === "approved") &&
                new Date(r.end_date) >= new Date() && (
                  <button
                    type="button"
                    onClick={() => void cancel(r.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    {t("leave.cancel")}
                  </button>
                )}
            </div>
          </div>

          {r.status === "amended" && r.amended_start_date && r.amended_end_date && (
            <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-xs font-semibold text-orange-800">
                {t("leave.managerAmended")}
              </p>
              <div className="mt-2 space-y-1 text-xs text-gray-700">
                <p>
                  {t("leave.originalRequest")}:{" "}
                  {DATE_FMT.format(new Date(r.start_date))} →{" "}
                  {DATE_FMT.format(new Date(r.end_date))} ({r.total_days}{" "}
                  {t("leave.days").toLowerCase()})
                </p>
                <p className="font-semibold text-orange-900">
                  {t("leave.proposedDates")}:{" "}
                  {DATE_FMT.format(new Date(r.amended_start_date))} →{" "}
                  {DATE_FMT.format(new Date(r.amended_end_date))} ({r.amended_total_days}{" "}
                  {t("leave.days").toLowerCase()})
                </p>
                {r.amendment_notes && (
                  <p className="italic text-gray-600">
                    {r.amended_by_name}: {r.amendment_notes}
                  </p>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => void acceptAmendment(r.id)}
                  className="min-h-[44px] flex-1 rounded-lg bg-green-600 text-sm font-semibold text-white"
                >
                  {t("leave.acceptChanges")}
                </button>
                <button
                  type="button"
                  onClick={() => void rejectAmendment(r.id)}
                  className="min-h-[44px] flex-1 rounded-lg bg-red-600 text-sm font-semibold text-white"
                >
                  {t("leave.declineChanges")}
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------- Balance ----------

function BalanceTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaveEntitlement[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setRows(await api.get<LeaveEntitlement[]>("/api/leave/balances"));
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">{t("leave.leaveType")}</th>
            <th className="px-3 py-2 text-right">
              {t("leave.balance.entitlement")}
            </th>
            <th className="px-3 py-2 text-right">
              {t("leave.balance.carriedOver")}
            </th>
            <th className="px-3 py-2 text-right">{t("leave.balance.used")}</th>
            <th className="px-3 py-2 text-right">
              {t("leave.balance.remaining")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="px-3 py-2">
                <span
                  aria-hidden
                  className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                  style={{ backgroundColor: r.leave_type_colour ?? "#0872c1" }}
                />
                {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {Number(r.total_days) > 0
                  ? r.total_days
                  : t("leave.balance.statutory")}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.carried_over_days}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.used_days}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">
                {Number(r.total_days) > 0 ? r.remaining_days : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Team Requests ----------

function TeamRequestsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [amendingId, setAmendingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(
        await api.get<LeaveRequest[]>(
          "/api/leave/team-requests?status=pending",
        ),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (id: number) => {
    const notes = prompt("Notes (optional):") || "";
    await api.put(`/api/leave/requests/${id}/approve`, { notes });
    await load();
  };

  const reject = async (id: number) => {
    const notes = prompt(t("leave.rejectReason")) || "";
    if (!notes.trim()) return;
    await api.put(`/api/leave/requests/${id}/reject`, { notes });
    await load();
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500">{t("leave.noRequests")}</p>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li
          key={r.id}
          className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <p className="text-sm font-semibold text-gray-900">
            {r.employee_name}{" "}
            <span className="text-xs text-gray-500">
              · {r.employee_number}
            </span>
          </p>
          <p className="text-xs text-gray-600">
            {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)} · {DATE_FMT.format(new Date(r.start_date))} →{" "}
            {DATE_FMT.format(new Date(r.end_date))} · {r.total_days}{" "}
            {t("leave.days").toLowerCase()}
          </p>
          {r.reason && <p className="mt-1 text-xs text-gray-600">{r.reason}</p>}

          {amendingId === r.id ? (
            <AmendForm
              request={r}
              onDone={() => {
                setAmendingId(null);
                void load();
              }}
              onCancel={() => setAmendingId(null)}
            />
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void approve(r.id)}
                className="min-h-[44px] rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t("leave.approve")}
              </button>
              <button
                type="button"
                onClick={() => setAmendingId(r.id)}
                className="min-h-[44px] rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t("leave.amend")}
              </button>
              <button
                type="button"
                onClick={() => void reject(r.id)}
                className="min-h-[44px] rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t("leave.reject")}
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function AmendForm({
  request,
  onDone,
  onCancel,
}: {
  request: LeaveRequest;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(request.start_date);
  const [endDate, setEndDate] = useState(request.end_date);
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!notes.trim()) {
      setError(t("leave.rejectReason"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/api/leave/requests/${request.id}/amend`, {
        start_date: startDate,
        end_date: endDate,
        is_half_day_start: halfStart,
        is_half_day_end: halfEnd,
        notes: notes.trim(),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg border border-orange-200 bg-orange-50/50 p-3">
      <p className="text-xs font-semibold text-orange-800">
        {t("leave.originalRequest")}: {DATE_FMT.format(new Date(request.start_date))} →{" "}
        {DATE_FMT.format(new Date(request.end_date))} ({request.total_days} {t("leave.days").toLowerCase()})
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-gray-700">{t("leave.startDate")}</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input mt-1"
          />
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-700">{t("leave.endDate")}</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input mt-1"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={halfStart} onChange={(e) => setHalfStart(e.target.checked)} />
          {t("leave.halfDayStart")}
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={halfEnd} onChange={(e) => setHalfEnd(e.target.checked)} />
          {t("leave.halfDayEnd")}
        </label>
      </div>
      <label className="block text-xs">
        <span className="font-medium text-gray-700">{t("leave.amendmentNotes")} *</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          required
          className="input mt-1"
        />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="min-h-[44px] rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {submitting ? t("common.loading") : t("leave.submitAmendment")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

// ---------- small helpers ----------

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
